/**
 * V2AdminReportPage — Grounded Leak Report renderer.
 *
 * Consumed by /admin/os/sessions/:sessionId (V2AdminOSDetailPage).
 * Renders the 11-section report bound STRICTLY to the fields produced by
 * discover_engine.generate_report (grounded pipeline).
 *
 * Design rules (Michael's brief):
 *  - Read ONLY from real engine fields; when a field is missing, show an
 *    honest "sizing pending" / "—" fallback. NEVER fabricate a $ or a
 *    benchmark client-side.
 *  - Premium dark cockpit theme on screen; clean light study theme for
 *    print via @media print. window.print() drives the PDF export.
 *
 * Sections (in order):
 *   1. Header               — business name, industry pack, timestamp
 *   2. Headline             — the one-line bottom-line
 *   3. Leak Picture Band    — 5 severity tiles at a glance
 *   4. Leak Map             — business_map stages arrow flow
 *   5. You vs Benchmark     — cited benchmarks + coverage bars
 *   6. Where it's Leaking   — leaks with severity >= low, grounded lines
 *   7. 2x2 Matrix           — Impact (severity) x Effort scatter
 *   8. Order of Attack      — plan_of_attack sequence + brief
 *   9. Coverage & Confidence — 0-100 scores per area + capped-by notes
 *  10. Deep Dive             — full per-leak recommendation blocks
 *  11. Talking Points        — the angles to lead with
 */
import React from 'react';
import { useReveal, Lively, CountUp } from './motion';

// ─── Cockpit palette (mirrors V2AdminOSPage — kept local to avoid circular
//     imports and to let the print media query override cleanly). ─────────
const C = {
  navy:       '#100838',
  surface:    '#1A0F44',
  hair:       'rgba(220,240,245,0.14)',
  hairBright: 'rgba(220,240,245,0.24)',
  icy:        '#EFF0FF',
  muted:      'rgba(239,240,255,0.82)',
  faint:      'rgba(239,240,255,0.62)',
  spectrum:   'linear-gradient(95deg, #34D1B0 0%, #1FB6C1 25%, #4F8FD0 50%, #9156C4 75%, #EB258D 100%)',
  print_ink:  '#1c1340',
};

const LEAK_COLOR = {
  time:        '#EB258D',
  money:       '#9156C4',
  lost_work:   '#4F8FD0',
  pricing:     '#1FB6C1',
  blind_spots: '#34D1B0',
};
const LEAK_LABEL = {
  time:        'Time',
  money:       'Money',
  lost_work:   'Lost Work',
  pricing:     'Pricing',
  blind_spots: 'Blind Spots',
};
const LEAK_ORDER = ['time', 'money', 'lost_work', 'pricing', 'blind_spots'];

const SEV = {
  high:   { label: 'High',       weight: 3, bg: 'rgba(235,37,141,0.18)', border: 'rgba(235,37,141,0.55)', text: '#FF7DB3', ink: '#B0176B' },
  medium: { label: 'Medium',     weight: 2, bg: 'rgba(145,86,196,0.18)', border: 'rgba(145,86,196,0.55)', text: '#B89CE0', ink: '#6C40A0' },
  low:    { label: 'Low',        weight: 1, bg: 'rgba(31,182,193,0.14)', border: 'rgba(31,182,193,0.50)', text: '#7BD3DA', ink: '#127a83' },
  none:   { label: 'No signal',  weight: 0, bg: 'rgba(220,240,245,0.06)', border: 'rgba(220,240,245,0.18)', text: 'rgba(220,240,245,0.55)', ink: '#7a7a85' },
};

const EFFORT = {
  quick_win:      { label: 'Quick win',       weight: 3, color: '#5FE0BE', bg: 'rgba(52,209,176,0.14)', border: 'rgba(52,209,176,0.45)', ink: '#0f7f66' },
  medium_build:   { label: 'Medium build',    weight: 2, color: '#9EC1E8', bg: 'rgba(79,143,208,0.14)', border: 'rgba(79,143,208,0.45)', ink: '#2f5e94' },
  bigger_project: { label: 'Bigger project',  weight: 1, color: '#B89CE0', bg: 'rgba(145,86,196,0.14)', border: 'rgba(145,86,196,0.45)', ink: '#6c40a0' },
};

const CONF_LABEL = { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence' };

// ─── Tiny atoms ─────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const industryLabel = (key) => {
  if (!key || key === 'general') return 'General SMB';
  return key.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const LeakDot = ({ area, size = 10, style = {} }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: '50%',
    background: LEAK_COLOR[area] || C.faint, marginRight: 8, verticalAlign: 'middle',
    ...style,
  }} />
);

const SeverityPill = ({ sev }) => {
  const s = SEV[sev] || SEV.none;
  return (
    <span
      className="v2gr-sev-pill"
      data-sev={sev}
      style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 999,
        fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        background: s.bg, color: s.text, border: `1px solid ${s.border}`,
      }}
    >{s.label}</span>
  );
};

const EffortPill = ({ effort }) => {
  const e = EFFORT[effort] || EFFORT.medium_build;
  return (
    <span
      className="v2gr-effort-pill"
      data-effort={effort}
      style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 999,
        fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 600,
        letterSpacing: '0.04em',
        background: e.bg, color: e.color, border: `1px solid ${e.border}`,
      }}
    >{e.label}</span>
  );
};

// ─── (Removed: legacy @media print light-theme CSS. The Download PDF button
//     now fetches a server-rendered on-brand dark PDF from the backend, so we
//     no longer inject a light-theme override for window.print(). If a user
//     hits Ctrl+P from the browser the on-screen dark UI is what they'll
//     get — which is fine as a fallback and matches brand.) ─────────────
const PrintStyles = () => null;


// ─── Section chrome ─────────────────────────────────────────────────────
const Section = ({ number, title, subtitle, testId, right, children }) => {
  const ref = useReveal(); // LIVELY — each report section reveals on scroll
  return (
  <section
    ref={ref}
    className="v2gr-section v2gr-card"
    data-testid={testId}
    style={{
      background: C.surface, border: `1px solid ${C.hair}`, borderRadius: 20,
      marginBottom: 18, overflow: 'hidden',
    }}
  >
    <header
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '16px 22px', borderBottom: `1px solid ${C.hair}`,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {number != null && (
          <span
            className="v2gr-section-num"
            style={{
              fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 10.5,
              letterSpacing: '0.18em', color: C.faint, background: 'rgba(220,240,245,0.05)',
              border: `1px solid ${C.hair}`, padding: '3px 8px', borderRadius: 999,
            }}
          >§{String(number).padStart(2, '0')}</span>
        )}
        <div>
          <h2
            className="v2gr-heading"
            style={{
              fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 600, color: C.icy,
              fontSize: 17, letterSpacing: '-0.005em', margin: 0,
            }}
          >{title}</h2>
          {subtitle && (
            <div
              className="v2gr-faint"
              style={{ marginTop: 3, fontFamily: 'Inter, system-ui', fontSize: 12, color: C.faint }}
            >{subtitle}</div>
          )}
        </div>
      </div>
      {right}
    </header>
    <div style={{ padding: '18px 22px' }}>{children}</div>
  </section>
  );
};

// ── § 01 Header ─────────────────────────────────────────────────────────
const HeaderStrip = ({ session, report, warnings }) => (
  <Section
    number={1}
    title="Report header"
    subtitle="Client, industry pack, and grounded-pipeline metadata"
    testId="v2gr-header"
  >
    <div
      className="v2gr-hero"
      style={{
        borderRadius: 16, padding: '18px 20px',
        background: 'linear-gradient(120deg, rgba(235,37,141,0.10), rgba(31,182,193,0.10))',
        border: `1px solid ${C.hair}`,
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
        <MetaCell label="Client" value={session?.business_name || session?.business_name_hint || (session?.linked ? 'Booked client' : 'Anonymous session')} testId="v2gr-meta-client" />
        <MetaCell label="Industry pack" value={industryLabel(report?.industry_classification)} testId="v2gr-meta-industry" />
        <MetaCell label="Discovery completed" value={fmtDate(session?.completed_at || session?.created_at)} testId="v2gr-meta-date" />
        <MetaCell label="Benchmarks cited" value={String((report?.benchmarks_cited || []).length)} testId="v2gr-meta-benchcount" />
      </div>
    </div>
    {Array.isArray(warnings) && warnings.length > 0 && (
      <div
        className="no-print"
        data-testid="v2gr-warnings"
        style={{
          marginTop: 14, padding: '12px 14px', borderRadius: 12,
          background: 'rgba(245,194,104,0.08)', border: '1px solid rgba(245,194,104,0.40)',
          color: '#F5C268', fontFamily: 'Inter, system-ui', fontSize: 12.5, lineHeight: 1.55,
        }}
      >
        <strong style={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 11 }}>
          Self-check warnings ·
        </strong>{' '}
        {warnings.length} soft issue{warnings.length === 1 ? '' : 's'} flagged during grounding validation.
        <ul style={{ margin: '8px 0 0 18px', padding: 0, listStyle: 'disc' }}>
          {warnings.map((w, i) => (
            <li key={i} style={{ margin: '3px 0' }}>{w}</li>
          ))}
        </ul>
      </div>
    )}
  </Section>
);

const MetaCell = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <div
      className="v2gr-faint"
      style={{
        fontFamily: 'Inter, system-ui', fontSize: 10.5, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: C.faint, fontWeight: 700,
      }}
    >{label}</div>
    <div
      className="v2gr-body v2gr-heading"
      style={{ marginTop: 6, fontFamily: 'Poppins, Inter, sans-serif', color: C.icy, fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}
    >{value || '—'}</div>
  </div>
);

// ── § 02 Headline ──────────────────────────────────────────────────────
const HeadlineSection = ({ report }) => (
  <Section
    number={2}
    title="Headline"
    subtitle="Michael's one-line bottom-line — the biggest leak + the unlock"
    testId="v2gr-headline-section"
  >
    <p
      className="v2gr-heading"
      data-testid="v2gr-headline"
      style={{
        fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 600, color: C.icy,
        fontSize: 26, lineHeight: 1.28, letterSpacing: '-0.015em', margin: 0,
      }}
    >
      {report?.headline || 'Report headline pending — regenerate to populate.'}
    </p>
  </Section>
);

// ── § 03 Leak Picture Band ─────────────────────────────────────────────
const LeakPictureBand = ({ report }) => {
  const leaks = LEAK_ORDER.map((area) => {
    const l = (report?.leak_report || []).find((x) => x.leak_area === area) || {
      leak_area: area, severity: 'none', confidence: 'low',
    };
    const cov = (report?.coverage_scores || {})[area];
    return { area, sev: l.severity || 'none', conf: l.confidence || 'low', coverage: typeof cov === 'number' ? cov : null };
  });

  return (
    <Section
      number={3}
      title="Leak picture at a glance"
      subtitle="Severity and coverage across the five standard leak areas"
      testId="v2gr-leak-band"
    >
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}
      >
        {leaks.map(({ area, sev, conf, coverage }) => {
          const s = SEV[sev] || SEV.none;
          return (
            <div
              key={area}
              className="v2gr-card"
              data-testid={`v2gr-band-${area}`}
              style={{
                background: 'rgba(220,240,245,0.03)', border: `1px solid ${C.hair}`,
                borderRadius: 14, padding: '14px 14px 12px 14px',
                borderTop: `3px solid ${LEAK_COLOR[area]}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <span
                  className="v2gr-heading"
                  style={{ fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 600, color: C.icy, fontSize: 14 }}
                >
                  <LeakDot area={area} />{LEAK_LABEL[area]}
                </span>
              </div>
              <div style={{ marginTop: 10 }}>
                <SeverityPill sev={sev} />
              </div>
              <div
                className="v2gr-faint"
                style={{ marginTop: 10, fontFamily: 'Inter, system-ui', fontSize: 11, color: C.faint, letterSpacing: '0.04em' }}
              >
                Coverage{' '}
                <span className="v2gr-body" style={{ color: C.icy, fontWeight: 700 }}>
                  {coverage == null ? '—' : <CountUp value={coverage} suffix="%" duration={900} />}
                </span>
                {' · '}
                <span className="v2gr-body" style={{ color: C.muted }}>{conf} conf</span>
              </div>
              <ProgressBar value={coverage} colour={LEAK_COLOR[area]} />
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const ProgressBar = ({ value, colour = C.icy }) => {
  const v = typeof value === 'number' ? Math.max(0, Math.min(100, value)) : 0;
  return (
    <div
      className="v2gr-bar-track"
      style={{
        marginTop: 8, height: 6, borderRadius: 999, background: 'rgba(220,240,245,0.08)', overflow: 'hidden',
      }}
    >
      <div
        className="v2gr-bar-fill"
        style={{ width: `${v}%`, height: '100%', background: colour, transition: 'width 240ms ease' }}
      />
    </div>
  );
};

// ── § 04 Leak Map ──────────────────────────────────────────────────────
const LeakMapSection = ({ report }) => {
  const stages = (report?.business_map || {}).stages || [];
  return (
    <Section
      number={4}
      title="Leak map"
      subtitle="The owner's flow — where things move cleanly vs where they leak"
      testId="v2gr-leak-map-section"
    >
      {stages.length === 0 ? (
        <p className="v2gr-faint" style={{ color: C.faint, fontFamily: 'Inter, system-ui', fontSize: 13 }}>
          No business map generated for this session yet.
        </p>
      ) : (
        <div
          className="v2gr-leak-map"
          style={{ overflowX: 'auto', paddingBottom: 6 }}
          data-testid="v2gr-leak-map"
        >
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 12, minWidth: 'max-content' }}>
            {stages.map((s, i) => {
              const isLeak = s.state === 'leaking';
              const col = isLeak ? (LEAK_COLOR[s.leak_area] || '#EB258D') : C.hairBright;
              return (
                <React.Fragment key={s.id || i}>
                  <div
                    className="v2gr-map-stage v2gr-card"
                    data-testid={`v2gr-map-stage-${s.id || i}`}
                    style={{
                      minWidth: 190, maxWidth: 240,
                      background: isLeak ? `${col}1a` : 'rgba(220,240,245,0.04)',
                      border: `1px solid ${isLeak ? `${col}66` : C.hair}`,
                      borderRadius: 14, padding: '14px 16px',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'Inter, system-ui', fontSize: 10.5, letterSpacing: '0.16em',
                        textTransform: 'uppercase', color: isLeak ? col : C.faint, fontWeight: 700,
                      }}
                    >
                      {isLeak ? 'Leaking' : 'Clean'}
                      {isLeak && s.leak_area ? ` · ${LEAK_LABEL[s.leak_area]}` : ''}
                    </div>
                    <div
                      className="v2gr-heading"
                      style={{
                        marginTop: 6, fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 600,
                        color: C.icy, fontSize: 15, lineHeight: 1.25,
                      }}
                    >{s.label || 'Unnamed stage'}</div>
                    {s.note && (
                      <div
                        className="v2gr-muted"
                        style={{ marginTop: 6, fontFamily: 'Inter, system-ui', fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}
                      >{s.note}</div>
                    )}
                    {s.source_quote && (
                      <div
                        className="v2gr-quote"
                        style={{
                          marginTop: 8, fontFamily: 'Inter, system-ui', fontSize: 11.5, color: C.faint,
                          fontStyle: 'italic', lineHeight: 1.55,
                          borderLeft: `2px solid ${C.hair}`, paddingLeft: 8,
                        }}
                      >“{s.source_quote}”</div>
                    )}
                  </div>
                  {i < stages.length - 1 && (
                    <div className="v2gr-map-arrow" style={{ display: 'flex', alignItems: 'center' }} aria-hidden>
                      <svg width="22" height="14" viewBox="0 0 22 14">
                        <path d="M1 7h18m0 0l-5-5m5 5l-5 5"
                              stroke={C.hairBright} strokeWidth="1.5" fill="none"
                              strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </Section>
  );
};

// ── § 05 You vs Benchmark ──────────────────────────────────────────────
const BenchmarkSection = ({ report }) => {
  const benchmarks = report?.benchmarks_cited || [];
  const scores = report?.coverage_scores || {};
  return (
    <Section
      number={5}
      title="You vs benchmark"
      subtitle="Every $ figure and comparison traces to a benchmark literally cited from the Kindra Brain"
      testId="v2gr-benchmark-section"
    >
      {benchmarks.length === 0 ? (
        <p className="v2gr-faint" style={{ color: C.faint, fontFamily: 'Inter, system-ui', fontSize: 13 }}>
          No benchmarks cited in this report — sizing pending until Michael confirms client numbers on the call.
        </p>
      ) : (
        <ul
          data-testid="v2gr-benchmark-list"
          style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 10 }}
        >
          {benchmarks.map((b, i) => (
            <li
              key={i}
              className="v2gr-benchmark-cite"
              data-testid={`v2gr-benchmark-${i}`}
              style={{
                fontFamily: 'Inter, system-ui', fontSize: 13.5, color: C.icy, lineHeight: 1.55,
                padding: '10px 12px', borderRadius: 10,
                background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`,
                borderLeft: '3px solid #34D1B0',
              }}
            >
              <span style={{ color: '#5FE0BE', fontFamily: 'Inter, system-ui', fontWeight: 700, marginRight: 8 }}>◆</span>
              {b}
            </li>
          ))}
        </ul>
      )}

      <div
        className="v2gr-faint"
        style={{
          marginTop: 16, fontFamily: 'Inter, system-ui', fontSize: 10.5, color: C.faint,
          letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700,
        }}
      >Coverage per leak area</div>
      <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
        {LEAK_ORDER.map((a) => {
          const v = scores[a];
          const val = typeof v === 'number' ? v : 0;
          return (
            <div key={a} data-testid={`v2gr-bench-cov-${a}`}
                 style={{ display: 'grid', gridTemplateColumns: '140px 1fr 48px', alignItems: 'center', gap: 12 }}>
              <div className="v2gr-body"
                   style={{ fontFamily: 'Inter, system-ui', fontSize: 13, color: C.icy }}>
                <LeakDot area={a} />{LEAK_LABEL[a]}
              </div>
              <ProgressBar value={val} colour={LEAK_COLOR[a]} />
              <div className="v2gr-body" style={{ fontFamily: 'Inter, system-ui', fontSize: 12, color: C.muted, textAlign: 'right' }}>
                {val}%
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

// ── § 06 Where it's Leaking ─────────────────────────────────────────────
const WhereItsLeaking = ({ report }) => {
  const active = (report?.leak_report || []).filter((l) => (l.severity || 'none') !== 'none');
  return (
    <Section
      number={6}
      title="Where it's leaking"
      subtitle={`${active.length} leak${active.length === 1 ? '' : 's'} with observed severity — each carries verbatim evidence`}
      testId="v2gr-where-leaking"
    >
      {active.length === 0 ? (
        <p className="v2gr-faint" style={{ color: C.faint, fontFamily: 'Inter, system-ui', fontSize: 13 }}>
          No leaks reached the severity threshold. See §10 for the full per-area detail.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {active.map((l) => (
            <div
              key={l.leak_area}
              className="v2gr-leak-card"
              data-testid={`v2gr-leak-${l.leak_area}`}
              style={{
                background: 'rgba(220,240,245,0.03)', border: `1px solid ${C.hair}`,
                borderRadius: 14, padding: '14px 16px',
                borderLeft: `3px solid ${LEAK_COLOR[l.leak_area]}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span
                  className="v2gr-heading"
                  style={{ fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 600, color: C.icy, fontSize: 15 }}
                >
                  <LeakDot area={l.leak_area} />{LEAK_LABEL[l.leak_area]}
                </span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <SeverityPill sev={l.severity} />
                  <span
                    className="v2gr-faint"
                    style={{
                      fontFamily: 'Inter, system-ui', fontSize: 10.5, color: C.faint,
                      padding: '3px 8px', borderRadius: 999, border: `1px solid ${C.hair}`,
                      letterSpacing: '0.06em',
                    }}
                  >{CONF_LABEL[l.confidence] || 'Low confidence'}</span>
                </div>
              </div>
              {l.summary && (
                <p
                  className="v2gr-body"
                  style={{ marginTop: 10, fontFamily: 'Inter, system-ui', fontSize: 13.5, color: C.muted, lineHeight: 1.6 }}
                >{l.summary}</p>
              )}
              {l.grounded_line && (
                <div
                  className="v2gr-benchmark-cite"
                  data-testid={`v2gr-grounded-${l.leak_area}`}
                  style={{
                    marginTop: 10, padding: '9px 12px', borderRadius: 8,
                    background: 'rgba(52,209,176,0.06)', border: '1px solid rgba(52,209,176,0.35)',
                    fontFamily: 'Inter, system-ui', fontSize: 12.5, color: '#5FE0BE', lineHeight: 1.55,
                  }}
                >
                  <strong style={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 10.5, marginRight: 6 }}>
                    Benchmark ·
                  </strong>{l.grounded_line}
                </div>
              )}
              {l.estimated_impact && (
                <div
                  className="v2gr-body"
                  style={{
                    marginTop: 10, fontFamily: 'Inter, system-ui', fontSize: 12.5, color: C.icy,
                    padding: '8px 10px', borderRadius: 8,
                    background: 'rgba(220,240,245,0.05)', border: `1px solid ${C.hair}`,
                  }}
                  data-testid={`v2gr-impact-${l.leak_area}`}
                >
                  <span className="v2gr-faint" style={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 10.5, fontWeight: 700, marginRight: 8, color: C.faint }}>
                    Estimated impact
                  </span>{l.estimated_impact}
                </div>
              )}
              {Array.isArray(l.evidence) && l.evidence.length > 0 && (
                <ul style={{ margin: '10px 0 0 0', padding: 0, listStyle: 'none' }}>
                  {l.evidence.map((q, j) => (
                    <li
                      key={j}
                      className="v2gr-quote"
                      style={{
                        fontFamily: 'Inter, system-ui', fontSize: 12.5, color: C.faint,
                        fontStyle: 'italic', lineHeight: 1.55, padding: '4px 0 4px 10px',
                        borderLeft: `2px solid ${C.hair}`, marginBottom: 4,
                      }}
                    >“{q}”</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

// ── § 07 2x2 Matrix (Impact × Effort) ──────────────────────────────────
const ImpactEffortMatrix = ({ report }) => {
  // X = effort weight (higher weight = quicker), Y = severity weight (higher = bigger impact)
  // Layout: X axis → Effort (Quick win | Medium build | Bigger project)  (left → right, quickest on left)
  //         Y axis → Impact (High at top, Low at bottom)
  // Top-LEFT (High × Quick win) = the cheapest big win.
  const leaks = (report?.leak_report || []).filter((l) => (l.severity || 'none') !== 'none');
  const rows = ['high', 'medium', 'low'];
  const cols = ['quick_win', 'medium_build', 'bigger_project']; // left → right (cheaper/faster on left)

  const inCell = (sev, effort) => leaks.filter(
    (l) => (l.severity || 'none') === sev && (l.recommendation?.effort || 'medium_build') === effort,
  );

  return (
    <Section
      number={7}
      title="Impact × effort matrix"
      subtitle="Where each leak sits — the top-left corner is the cheapest big win"
      testId="v2gr-matrix"
    >
      {leaks.length === 0 ? (
        <p className="v2gr-faint" style={{ color: C.faint, fontFamily: 'Inter, system-ui', fontSize: 13 }}>
          No leaks with severity to plot yet.
        </p>
      ) : (
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '110px repeat(3, 1fr)',
              gridTemplateRows: 'auto repeat(3, 130px)',
              gap: 8,
            }}
          >
            {/* Header row */}
            <div />
            {cols.map((c) => (
              <div key={c}
                   className="v2gr-faint"
                   style={{
                     fontFamily: 'Inter, system-ui', fontSize: 10.5, letterSpacing: '0.14em',
                     textTransform: 'uppercase', color: C.faint, fontWeight: 700, textAlign: 'center',
                     alignSelf: 'end', paddingBottom: 6,
                   }}>{EFFORT[c].label}</div>
            ))}
            {/* Grid cells */}
            {rows.map((r) => (
              <React.Fragment key={r}>
                <div
                  className="v2gr-faint"
                  style={{
                    fontFamily: 'Inter, system-ui', fontSize: 10.5, letterSpacing: '0.14em',
                    textTransform: 'uppercase', color: C.faint, fontWeight: 700,
                    alignSelf: 'center', textAlign: 'right', paddingRight: 8,
                  }}>{SEV[r].label} impact</div>
                {cols.map((c) => {
                  const items = inCell(r, c);
                  const isTopLeft = r === 'high' && c === 'quick_win';
                  return (
                    <div
                      key={`${r}-${c}`}
                      className="v2gr-matrix-cell"
                      data-testid={`v2gr-matrix-${r}-${c}`}
                      style={{
                        background: isTopLeft ? 'rgba(52,209,176,0.06)' : 'rgba(220,240,245,0.03)',
                        border: `1px solid ${isTopLeft ? 'rgba(52,209,176,0.35)' : C.hair}`,
                        borderRadius: 12, padding: 10,
                        display: 'flex', flexWrap: 'wrap', gap: 8, alignContent: 'flex-start',
                      }}
                    >
                      {items.map((l) => (
                        <div
                          key={l.leak_area}
                          className="v2gr-matrix-dot"
                          title={`${LEAK_LABEL[l.leak_area]} — ${SEV[l.severity].label} × ${EFFORT[l.recommendation?.effort || 'medium_build'].label}`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '4px 10px 4px 6px', borderRadius: 999,
                            background: `${LEAK_COLOR[l.leak_area]}1e`,
                            border: `1px solid ${LEAK_COLOR[l.leak_area]}55`,
                          }}
                        >
                          <span style={{
                            width: 8, height: 8, borderRadius: '50%',
                            background: LEAK_COLOR[l.leak_area],
                          }} />
                          <span className="v2gr-body" style={{ fontFamily: 'Inter, system-ui', fontSize: 12, color: C.icy, fontWeight: 600 }}>
                            {LEAK_LABEL[l.leak_area]}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
          <div className="v2gr-faint" style={{ marginTop: 12, fontFamily: 'Inter, system-ui', fontSize: 11.5, color: C.faint, textAlign: 'center' }}>
            ← Quick win · Medium build · Bigger project →
          </div>
        </div>
      )}
    </Section>
  );
};

// ── § 08 Order of Attack ───────────────────────────────────────────────
const OrderOfAttack = ({ report }) => {
  const plan = report?.plan_of_attack || {};
  const seq = plan.sequence || [];
  const brief = plan.how_id_approach_this || '';

  return (
    <Section
      number={8}
      title="Order of attack"
      subtitle="Michael's 20-second pre-call brief · sequenced value-first"
      testId="v2gr-order-of-attack"
    >
      {brief && (
        <div
          className="v2gr-card"
          data-testid="v2gr-approach"
          style={{
            padding: '14px 18px', borderRadius: 12,
            background: 'rgba(235,37,141,0.06)', border: `1px solid ${C.hair}`,
            borderLeft: '3px solid #EB258D', marginBottom: seq.length > 0 ? 14 : 0,
          }}
        >
          <div
            className="v2gr-faint"
            style={{
              fontFamily: 'Inter, system-ui', fontSize: 10.5, letterSpacing: '0.16em',
              textTransform: 'uppercase', color: '#FF8FC1', fontWeight: 700, marginBottom: 6,
            }}
          >How I&apos;d approach this build</div>
          <p
            className="v2gr-body"
            style={{
              fontFamily: 'Inter, system-ui', fontSize: 14.5, color: C.icy,
              lineHeight: 1.65, whiteSpace: 'pre-wrap', margin: 0,
            }}
          >{brief}</p>
        </div>
      )}
      {seq.length === 0 && !brief && (
        <p className="v2gr-faint" style={{ color: C.faint, fontFamily: 'Inter, system-ui', fontSize: 13 }}>
          No order-of-attack sequence generated yet.
        </p>
      )}
      {seq.length > 0 && (
        <ol data-testid="v2gr-sequence" style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 10 }}>
          {seq.map((step, i) => (
            <li
              key={i}
              className="v2gr-attack-step v2gr-card"
              data-testid={`v2gr-step-${step.priority || i + 1}`}
              style={{
                background: 'rgba(220,240,245,0.03)', border: `1px solid ${C.hair}`,
                borderRadius: 12, padding: '12px 16px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span
                  className="v2gr-heading"
                  style={{
                    fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 700, color: '#ffffff',
                    fontSize: 13, width: 26, height: 26, display: 'inline-flex', alignItems: 'center',
                    justifyContent: 'center', borderRadius: '50%',
                    background: LEAK_COLOR[step.leak_area] || 'rgba(220,240,245,0.15)',
                  }}
                >{step.priority || i + 1}</span>
                <span
                  className="v2gr-heading"
                  style={{ fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 600, color: C.icy, fontSize: 15 }}
                >
                  <LeakDot area={step.leak_area} />{LEAK_LABEL[step.leak_area] || 'Leak area'}
                </span>
              </div>
              {step.why_this_order && (
                <div
                  className="v2gr-muted"
                  style={{
                    marginTop: 6, fontFamily: 'Inter, system-ui', fontSize: 13, color: C.muted,
                    lineHeight: 1.55, paddingLeft: 38,
                  }}
                >{step.why_this_order}</div>
              )}
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
};

// ── § 09 Coverage & Confidence ─────────────────────────────────────────
const CoverageConfidence = ({ report, session }) => {
  const scores = report?.coverage_scores || {};
  const notes = report?.coverage_notes || {};
  const conf = session?.confidence || {};
  return (
    <Section
      number={9}
      title="Coverage & confidence"
      subtitle="How thoroughly the chat explored each area — capped-by notes name the missing number"
      testId="v2gr-coverage"
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        {LEAK_ORDER.map((a) => {
          const v = scores[a];
          const val = typeof v === 'number' ? v : 0;
          const note = notes[a] || '';
          const c = conf[a] || 'low';
          return (
            <div
              key={a}
              data-testid={`v2gr-cov-${a}`}
              className="v2gr-card"
              style={{
                background: 'rgba(220,240,245,0.03)', border: `1px solid ${C.hair}`,
                borderRadius: 12, padding: '14px 16px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span
                  className="v2gr-heading"
                  style={{ fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 600, color: C.icy, fontSize: 14 }}
                >
                  <LeakDot area={a} />{LEAK_LABEL[a]}
                </span>
                <span
                  className="v2gr-heading"
                  style={{ fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 700, color: C.icy, fontSize: 22 }}
                >{val}<span className="v2gr-faint" style={{ fontSize: 12, color: C.faint, marginLeft: 2 }}>%</span></span>
              </div>
              <ProgressBar value={val} colour={LEAK_COLOR[a]} />
              <div
                className="v2gr-faint"
                style={{
                  marginTop: 8, fontFamily: 'Inter, system-ui', fontSize: 11, color: C.faint,
                  letterSpacing: '0.06em',
                }}
              >{CONF_LABEL[c] || 'Low confidence'}</div>
              {note && (
                <div
                  className="v2gr-muted"
                  style={{
                    marginTop: 8, fontFamily: 'Inter, system-ui', fontSize: 12.5, color: C.muted,
                    lineHeight: 1.5,
                  }}
                >
                  <span className="v2gr-faint" style={{ fontWeight: 700, color: C.faint }}>Capped by · </span>
                  {note}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
};

// ── § 10 Deep Dive ─────────────────────────────────────────────────────
// Compose the client-safe summary block, ordered by plan_of_attack.sequence.
// STRICTLY client-safe — only client_facing.title + client_facing.summary
// per leak. Never includes internal framing (own_it_note, self-check
// warnings, effort tags, $ figures from estimated_impact, or evidence).
// Leaks with no client_facing pair are skipped gracefully.
const composeClientSafeSummary = (report, businessName) => {
  const leaks = report?.leak_report || [];
  const byArea = {};
  for (const l of leaks) {
    if (l && l.leak_area) byArea[l.leak_area] = l;
  }
  const seq = (report?.plan_of_attack?.sequence || [])
    .map((s) => s.leak_area)
    .filter((a) => byArea[a]);
  // Fall back to canonical order for any areas the sequence omits.
  const ordered = [...seq, ...LEAK_ORDER.filter((a) => !seq.includes(a) && byArea[a])];

  const blocks = [];
  for (const area of ordered) {
    const l = byArea[area];
    const cf = l?.client_facing || {};
    const title = (cf.title || '').trim();
    const summary = (cf.summary || '').trim();
    if (!title && !summary) continue;
    const heading = title || LEAK_LABEL[area] || 'Recommended fix';
    blocks.push(summary ? `${heading}\n${summary}` : heading);
  }
  if (blocks.length === 0) return '';
  const biz = (businessName || '').trim();
  const preamble = biz
    ? `Here's the shortlist of what we'd build for ${biz}, in the order we'd tackle it:`
    : `Here's the shortlist of what we'd build, in the order we'd tackle it:`;
  return `${preamble}\n\n${blocks.join('\n\n')}\n`;
};

const CopyClientSafeButton = ({ report, businessName }) => {
  const [state, setState] = React.useState('idle'); // idle | copied | empty | error
  const onCopy = async () => {
    const text = composeClientSafeSummary(report, businessName);
    if (!text) {
      setState('empty');
      window.setTimeout(() => setState('idle'), 1800);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setState('copied');
    } catch {
      // Fallback for older browsers / non-secure contexts.
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setState('copied');
      } catch {
        setState('error');
      }
    }
    window.setTimeout(() => setState('idle'), 1800);
  };
  const label =
    state === 'copied' ? 'Copied ✓'
    : state === 'empty' ? 'Nothing to copy'
    : state === 'error' ? 'Copy failed'
    : 'Copy client-safe summary';
  return (
    <button
      type="button"
      className="no-print"
      onClick={onCopy}
      data-testid="v2gr-copy-client-safe-btn"
      style={{
        padding: '7px 14px', borderRadius: 999,
        border: `1px solid ${state === 'copied' ? 'rgba(52,209,176,0.55)' : C.hairBright}`,
        background: state === 'copied' ? 'rgba(52,209,176,0.10)' : 'transparent',
        color: state === 'copied' ? '#5FE0BE' : C.icy,
        fontFamily: 'Inter, system-ui', fontWeight: 600, fontSize: 12,
        letterSpacing: '0.02em', cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >{label}</button>
  );
};

const DeepDive = ({ report, businessName }) => {
  const leaks = LEAK_ORDER.map((area) => (
    (report?.leak_report || []).find((l) => l.leak_area === area) || {
      leak_area: area, severity: 'none', confidence: 'low', summary: '',
      evidence: [], grounded_line: '', estimated_impact: '',
      recommendation: { what_to_do: '', how_to_do_it: '', effort: 'medium_build', impact: '', own_it_note: '' },
      client_facing: { title: '', summary: '' },
    }
  ));
  return (
    <Section
      number={10}
      title="Deep dive · per-leak recommendation"
      subtitle="All 5 areas — what to build, how, why it matters, and the fast-path vs own-it framing"
      testId="v2gr-deep-dive"
      right={<CopyClientSafeButton report={report} businessName={businessName} />}
    >
      <Lively staggerChildren style={{ display: 'grid', gap: 14 }}>
        {leaks.map((l) => {
          const rec = l.recommendation || {};
          const cf = l.client_facing || {};
          const nothing = (l.severity || 'none') === 'none' && !rec.what_to_do && !l.summary;
          return (
            <div
              key={l.leak_area}
              className="v2gr-leak-card"
              data-testid={`v2gr-deep-${l.leak_area}`}
              style={{
                background: 'rgba(220,240,245,0.03)', border: `1px solid ${C.hair}`,
                borderRadius: 14, padding: '16px 18px',
                borderLeft: `3px solid ${LEAK_COLOR[l.leak_area]}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span
                  className="v2gr-heading"
                  style={{ fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 600, color: C.icy, fontSize: 16 }}
                >
                  <LeakDot area={l.leak_area} />{LEAK_LABEL[l.leak_area]}
                </span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <SeverityPill sev={l.severity} />
                  <EffortPill effort={rec.effort} />
                </div>
              </div>

              {nothing ? (
                <p
                  className="v2gr-faint"
                  style={{ marginTop: 10, fontFamily: 'Inter, system-ui', fontSize: 13, color: C.faint, fontStyle: 'italic' }}
                >
                  Sizing pending — needs more discovery on the call.
                </p>
              ) : (
                <>
                  {rec.what_to_do && (
                    <div style={{ marginTop: 12 }}>
                      <FieldLabel>What to do</FieldLabel>
                      <p className="v2gr-body"
                         style={{ fontFamily: 'Inter, system-ui', fontSize: 14, color: C.icy, lineHeight: 1.55, margin: '4px 0 0 0' }}>
                        {rec.what_to_do}
                      </p>
                    </div>
                  )}
                  {rec.how_to_do_it && (
                    <div style={{ marginTop: 10 }}>
                      <FieldLabel>How to do it</FieldLabel>
                      <p className="v2gr-muted"
                         style={{ fontFamily: 'Inter, system-ui', fontSize: 13.5, color: C.muted, lineHeight: 1.6, margin: '4px 0 0 0', whiteSpace: 'pre-wrap' }}>
                        {rec.how_to_do_it}
                      </p>
                    </div>
                  )}
                  {rec.impact && (
                    <div style={{ marginTop: 10 }}>
                      <FieldLabel>Why it matters</FieldLabel>
                      <p className="v2gr-muted"
                         style={{ fontFamily: 'Inter, system-ui', fontSize: 13, color: C.muted, lineHeight: 1.55, margin: '4px 0 0 0' }}>
                        {rec.impact}
                      </p>
                    </div>
                  )}
                  {rec.own_it_note && (
                    <div
                      className="no-print v2gr-card"
                      data-testid={`v2gr-own-it-${l.leak_area}`}
                      style={{
                        marginTop: 12, padding: '10px 12px', borderRadius: 8,
                        background: 'rgba(52,209,176,0.06)', border: '1px dashed rgba(52,209,176,0.45)',
                      }}
                    >
                      <FieldLabel style={{ color: '#5FE0BE' }}>Internal · fast-path vs own-it</FieldLabel>
                      <p className="v2gr-body"
                         style={{ fontFamily: 'Inter, system-ui', fontSize: 13, color: C.icy, lineHeight: 1.55, margin: '4px 0 0 0', whiteSpace: 'pre-wrap' }}>
                        {rec.own_it_note}
                      </p>
                    </div>
                  )}
                  {(cf.title || cf.summary) && (
                    <div
                      className="v2gr-card"
                      data-testid={`v2gr-client-facing-${l.leak_area}`}
                      style={{
                        marginTop: 12, padding: '10px 12px', borderRadius: 8,
                        background: 'rgba(220,240,245,0.05)', border: `1px solid ${C.hair}`,
                      }}
                    >
                      <FieldLabel>Client-facing · proposal snippet</FieldLabel>
                      {cf.title && (
                        <div className="v2gr-heading"
                             style={{ marginTop: 4, fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 600, color: C.icy, fontSize: 14 }}>
                          {cf.title}
                        </div>
                      )}
                      {cf.summary && (
                        <p className="v2gr-muted"
                           style={{ margin: '4px 0 0 0', fontFamily: 'Inter, system-ui', fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>
                          {cf.summary}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </Lively>
    </Section>
  );
};

const FieldLabel = ({ children, style = {} }) => (
  <div
    className="v2gr-faint"
    style={{
      fontFamily: 'Inter, system-ui', fontSize: 10.5, letterSpacing: '0.16em',
      textTransform: 'uppercase', color: C.faint, fontWeight: 700, ...style,
    }}
  >{children}</div>
);

// ── § 11 Talking Points ────────────────────────────────────────────────
const TalkingPoints = ({ report }) => {
  const points = report?.talking_points_for_call || [];
  return (
    <Section
      number={11}
      title="Talking points for the call"
      subtitle="Quote-anchored or number-anchored — the angles to lead with"
      testId="v2gr-talking-points"
    >
      {points.length === 0 ? (
        <p className="v2gr-faint" style={{ color: C.faint, fontFamily: 'Inter, system-ui', fontSize: 13 }}>
          No talking points generated for this session yet.
        </p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
          {points.map((tp, i) => (
            <li
              key={i}
              data-testid={`v2gr-talk-${i}`}
              className="v2gr-body"
              style={{
                fontFamily: 'Inter, system-ui', fontSize: 14, color: C.icy, lineHeight: 1.55,
                padding: '10px 12px', borderRadius: 10,
                background: 'rgba(220,240,245,0.04)', border: `1px solid ${C.hair}`,
                borderLeft: '3px solid #EB258D',
              }}
            >
              <span style={{ marginRight: 10, color: '#EB258D', fontWeight: 700 }}>›</span>{tp}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
};

// ─── Download PDF button — fetches server-rendered on-brand PDF and
//     triggers a real file download (no print dialog). ─────────────────
export const PrintPdfButton = ({ sessionId, businessName, label = 'Download PDF' }) => {
  const [state, setState] = React.useState('idle'); // idle | busy | done | error
  const onDownload = async () => {
    if (!sessionId) { setState('error'); window.setTimeout(() => setState('idle'), 1800); return; }
    setState('busy');
    try {
      const token = localStorage.getItem('kindra-admin-token') || '';
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(
        `${base}/api/admin/os/sessions/${encodeURIComponent(sessionId)}/report.pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeBiz = (businessName || 'Anonymous').replace(/[^\w \-]/g, '').trim().replace(/\s+/g, '-') || 'Anonymous';
      a.download = `Kindra-Leak-Report-${safeBiz}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => URL.revokeObjectURL(url), 5000);
      setState('done');
    } catch {
      setState('error');
    }
    window.setTimeout(() => setState('idle'), 2000);
  };
  const text =
    state === 'busy'  ? 'Preparing PDF…'
    : state === 'done'  ? 'PDF downloaded ✓'
    : state === 'error' ? 'Download failed'
    : label;
  return (
    <button
      type="button"
      className="no-print"
      onClick={onDownload}
      disabled={state === 'busy'}
      data-testid="v2gr-print-pdf-btn"
      style={{
        padding: '10px 18px', borderRadius: 999,
        border: `1px solid ${state === 'done' ? 'rgba(52,209,176,0.55)' : C.hairBright}`,
        background: state === 'done' ? 'rgba(52,209,176,0.10)' : 'transparent',
        color: state === 'done' ? '#5FE0BE' : C.icy,
        fontFamily: 'Inter, system-ui', fontWeight: 600, fontSize: 13,
        cursor: state === 'busy' ? 'wait' : 'pointer', whiteSpace: 'nowrap',
      }}
    >
      {text}
    </button>
  );
};

// ─── Main grounded-report view (11 sections) ────────────────────────────
export const V2GroundedReport = ({ session, report }) => {
  if (!report) return null;
  const warnings = report._self_check_warnings || [];
  return (
    <div className="v2gr-print-root" data-testid="v2gr-root">
      <PrintStyles />
      <HeaderStrip session={session} report={report} warnings={warnings} />
      <HeadlineSection report={report} />
      <LeakPictureBand report={report} />
      <LeakMapSection report={report} />
      <BenchmarkSection report={report} />
      <WhereItsLeaking report={report} />
      <ImpactEffortMatrix report={report} />
      <OrderOfAttack report={report} />
      <CoverageConfidence report={report} session={session} />
      <DeepDive report={report} businessName={session?.business_name || session?.business_name_hint || ''} />
      <TalkingPoints report={report} />
    </div>
  );
};

export default V2GroundedReport;
